/**
 * Admin > Patreon page.
 *
 * Table of site features (rows) and Script Hive campaign tiers (columns),
 * including any $0 / "free" campaign tiers. There is no separate Free column —
 * those values live on Admin > Benefits.
 *
 * Values are edited locally in `draft`, then Save writes them to
 * patreon_tier_features only. It does not copy benefits onto users.
 * Discard restores the last loaded / saved baseline.
 */
import { useEffect, useState } from "react";
import { Table, Button, Form } from "react-bootstrap";

import {
    getPatreonCampaignTiers,
    savePatreonTierFeatures,
    PatreonCampaignTier,
    PatreonTiersResponse,
} from "../utils/Patreon";
import {
    BENEFIT_REGISTRY,
    BenefitId,
    BenefitValue,
    BenefitsByTier,
    BenefitMap,
    isValidBenefitValue,
    mergeTierBenefits,
} from "../utils/Benefits";
import { LoadingRing } from "../components/LoadingRing";

// null means the number box was cleared and needs a value before Save.
type DraftFeatureMap = Record<BenefitId, BenefitValue | null>;
type DraftByTier = Record<string, DraftFeatureMap>;

function formatAmount(cents: number | null): string {
    if (cents === null || cents === undefined) {
        return "-";
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(cents / 100);
}

/** Remove emoji / pictographs from Patreon tier titles for the table header. */
function stripEmoji(text: string): string {
    return text
        .replace(/\p{Extended_Pictographic}/gu, "")
        .replace(/\uFE0F/g, "")
        .replace(/\s+/g, " ")
        .trim();
}

function isPublishedTier(tier: PatreonCampaignTier): boolean {
    return tier.published !== false;
}

const TIER_TITLE_MAX_CHARS = 15;

function formatTierTitle(title: string | null): { display: string; full: string } {
    const full = stripEmoji(title || "").trim() || "-";
    if (full.length <= TIER_TITLE_MAX_CHARS) {
        return { display: full, full };
    }
    return {
        display: `${full.slice(0, TIER_TITLE_MAX_CHARS)}…`,
        full,
    };
}

function toDraftMap(features: BenefitMap): DraftFeatureMap {
    return { ...features };
}

/** Set up one column per tier, using saved values when we have them. */
function buildDraft(
    tierIds: string[],
    stored: BenefitsByTier | undefined,
): DraftByTier {
    const draft: DraftByTier = {};

    for (const tierId of tierIds) {
        draft[tierId] = toDraftMap(mergeTierBenefits(stored?.[tierId]));
    }

    return draft;
}

function draftsEqual(a: DraftByTier, b: DraftByTier): boolean {
    const aKeys = Object.keys(a).sort();
    const bKeys = Object.keys(b).sort();
    if (aKeys.length !== bKeys.length || aKeys.some((key, i) => key !== bKeys[i])) {
        return false;
    }

    for (const tierId of aKeys) {
        for (const feature of BENEFIT_REGISTRY) {
            if (a[tierId][feature.id] !== b[tierId][feature.id]) {
                return false;
            }
        }
    }

    return true;
}

/** Copy the current values so Discard can put them back cleanly. */
function cloneDraft(draft: DraftByTier): DraftByTier {
    const cloned: DraftByTier = {};
    for (const [tierId, features] of Object.entries(draft)) {
        cloned[tierId] = { ...features };
    }
    return cloned;
}

export default function AdminPatreon() {
    const [data, setData] = useState<PatreonTiersResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showUnpublishedTiers, setShowUnpublishedTiers] = useState(false);
    // Last saved (or freshly loaded) values — used to spot unsaved edits.
    const [baseline, setBaseline] = useState<DraftByTier>({});
    // What the admin is editing right now. Only written when they click Save.
    const [draft, setDraft] = useState<DraftByTier>({});

    async function load(force = false) {
        // On first load, skip the "unsaved changes?" prompt.
        if (!force && !draftsEqual(draft, baseline)) {
            const confirmed = window.confirm(
                "You have unsaved changes. Refresh and discard them?",
            );
            if (!confirmed) {
                return;
            }
        }

        setLoading(true);
        const result = await getPatreonCampaignTiers();
        setData(result);

        // Keep settings for every tier (including unpublished) so toggling the
        // show control does not lose values.
        const tierIds = (result?.tiers ?? []).map((tier) => tier.id);
        const next = buildDraft(tierIds, result?.features);
        setBaseline(next);
        setDraft(cloneDraft(next));
        setLoading(false);
    }

    // Mount-only: refresh is available via the Refresh button.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot load
    useEffect(() => { load(true); }, []);

    const allTiers: PatreonCampaignTier[] = data?.tiers ?? [];
    const tiers: PatreonCampaignTier[] = showUnpublishedTiers
        ? allTiers
        : allTiers.filter(isPublishedTier);
    const columnTierIds = tiers.map((tier) => tier.id);
    const dirty = !draftsEqual(draft, baseline);

    function setCellValue(
        tierId: string,
        featureId: BenefitId,
        value: BenefitValue | null,
    ) {
        setDraft((prev) => ({
            ...prev,
            [tierId]: {
                ...prev[tierId],
                [featureId]: value,
            },
        }));
    }

    function discard() {
        setDraft(cloneDraft(baseline));
    }

    async function save() {
        // Save every loaded campaign tier (including unpublished, even if hidden).
        const saveTierIds = allTiers.map((tier) => tier.id);
        if (saveTierIds.length === 0) {
            return;
        }

        // Don't save if a number field is blank or not a whole number.
        for (const tierId of saveTierIds) {
            const features = draft[tierId];
            if (!features) {
                continue;
            }
            for (const feature of BENEFIT_REGISTRY) {
                if (!isValidBenefitValue(feature.id, features[feature.id])) {
                    window.alert(
                        `Invalid value for ${feature.label} (${tierId}).`,
                    );
                    return;
                }
            }
        }

        const payload: BenefitsByTier = {};
        for (const tierId of saveTierIds) {
            const features = draft[tierId];
            const row: Partial<BenefitMap> = {};
            for (const feature of BENEFIT_REGISTRY) {
                const value = features[feature.id];
                if (value !== null) {
                    row[feature.id] = value;
                }
            }
            payload[tierId] = row;
        }

        setSaving(true);
        const result = await savePatreonTierFeatures(payload);
        setSaving(false);

        if (!result) {
            return;
        }

        // After a successful save, treat the new values as the starting point.
        const next = buildDraft(
            allTiers.map((tier) => tier.id),
            result.features,
        );
        setBaseline(next);
        setDraft(cloneDraft(next));
        setData((prev) => (prev ? { ...prev, features: result.features } : prev));
    }

    const columnBorderStyle = { borderLeft: "1px solid var(--bs-border-color, #dee2e6)" };

    return (
        <>
            <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                <div>
                    <h5 className="mb-1">Patreon Tier Benefits</h5>
                    <p className="text-muted small mb-0">
                        This table shows the Script Hive tiers from your Patreon campaign ID: {data?.campaign_id}.
                    </p>
                    <p className="text-muted small mb-0">
                        This is where you assign site features that you would like the users to 
                        have based on their Patreon tier.
                    </p>
                </div>
                {/* Only show Save / Discard when something has changed. */}
                {dirty && (
                    <>
                        <Button variant="outline-secondary" size="sm" disabled={loading || saving} onClick={discard}>
                            Discard
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={loading || saving || !data}
                            onClick={save}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </>
                )}
                <Form.Check
                    type="checkbox"
                    id="show-unpublished-tiers"
                    className="ms-auto"
                    label="Show Unpublished Tiers"
                    checked={showUnpublishedTiers}
                    disabled={loading}
                    onChange={(e) => setShowUnpublishedTiers(e.target.checked)}
                />
            </div>

            {loading ? (
                <LoadingRing />
            ) : !data ? (
                <p className="text-muted">
                    Unable to load Patreon tiers. Ensure the creator access token is configured
                    (<code>PATREON_CREATOR_ACCESS_TOKEN</code>) in Supabase Edge Function secrets.
                </p>
            ) : (
                <div className="table-responsive">
                    <Table striped hover size="sm" className="align-middle">
                        <thead>
                            <tr>
                                <th style={{ minWidth: "180px", verticalAlign: "top", padding: "0.85rem" }}>Feature</th>
                                {tiers.map((tier) => {
                                    const { display, full } = formatTierTitle(tier.title);

                                    return (
                                        <th
                                            key={tier.id}
                                            className="text-center"
                                            style={{
                                                minWidth: "110px",
                                                maxWidth: "120px",
                                                verticalAlign: "top",
                                                padding: "0.85rem",
                                                ...columnBorderStyle,
                                            }}
                                        >
                                            <div title={full}>{display}</div>
                                            <div className="small text-muted fw-normal">
                                                {formatAmount(tier.amount_cents)}
                                            </div>
                                            <div className="small fw-normal">
                                                <code>{tier.id}</code>
                                            </div>
                                            {tier.published === false && (
                                                <span
                                                    className="badge mt-1"
                                                    style={{
                                                        backgroundColor: "#495057",
                                                        color: "#fff",
                                                    }}
                                                >
                                                    Unpublished
                                                </span>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {BENEFIT_REGISTRY.map((feature) => (
                                <tr key={feature.id}>
                                    <td style={{ padding: "0.75rem" }}>
                                        <div className="fw-bold">{feature.label}</div>
                                        {feature.description && (
                                            <div className="small text-muted">{feature.description}</div>
                                        )}
                                    </td>
                                    {columnTierIds.map((tierId) => {
                                        const value = draft[tierId]?.[feature.id];
                                        const baselineValue = baseline[tierId]?.[feature.id];
                                        const changed = value !== baselineValue;
                                        // Soft highlight so unsaved edits are easy to spot.
                                        const cellStyle = {
                                            ...columnBorderStyle,
                                            padding: "0.75rem",
                                            ...(changed
                                                ? { backgroundColor: "rgba(255, 193, 7, 0.15)" }
                                                : {}),
                                        };

                                        if (feature.valueType === "boolean") {
                                            return (
                                                <td key={tierId} className="text-center" style={cellStyle}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={Boolean(value)}
                                                        disabled={saving}
                                                        onChange={(e) => setCellValue(tierId, feature.id, e.target.checked)}
                                                        aria-label={`${feature.label} for ${tierId}`}
                                                    />
                                                </td>
                                            );
                                        }

                                        return (
                                            <td key={tierId} className="text-center" style={cellStyle}>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min={0}
                                                    step={1}
                                                    value={typeof value === "number" ? value : ""}
                                                    disabled={saving}
                                                    className="text-end mx-auto"
                                                    style={{ maxWidth: "88px" }}
                                                    onChange={(e) => {
                                                        // Clearing the box marks it as incomplete until a number is typed.
                                                        if (e.target.value === "") {
                                                            setCellValue(tierId, feature.id, null);
                                                            return;
                                                        }
                                                        const next = Number.parseInt(e.target.value, 10);
                                                        setCellValue(
                                                            tierId,
                                                            feature.id,
                                                            Number.isFinite(next) ? next : null,
                                                        );
                                                    }}
                                                    aria-label={`${feature.label} for ${tierId}`}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </>
    );
}
